// covered-by: frontends/carrie/src/services/__tests__/askPunttProjects.test.ts
// covered-by: qa/rickrubin/tests/api/ask-puntt-projects.spec.ts
import {
  type AppendApprovalEventResponse,
  type GetApprovalParticipationResponse,
} from "@mg/schemas/src/christo/approvalEvent";
import {
  type AskPunttProjectScope,
  type ListAskPunttProjectsResponse,
  type GetAskPunttProjectResponse,
  type CreateAskPunttProjectResponse,
  type UpdateAskPunttProjectResponse,
  type DeleteAskPunttProjectResponse,
  type AddAskPunttProjectMemberResponse,
  type RemoveAskPunttProjectMemberResponse,
  type AddAskPunttProjectFileResponse,
  type AddAskPunttProjectFileVersionResponse,
  type ListAskPunttProjectChatsResponse,
  type AddChatToAskPunttProjectResponse,
  type RemoveChatFromAskPunttProjectResponse,
  type ShareAskPunttProjectChatResponse,
} from "@mg/schemas/src/christo/askPunttProject";
import { type PromoteChatToProjectResponse } from "@mg/schemas/src/christo/askPunttProjectFeed";
import {
  type ListParticipationTasksResponse,
  type ParticipationTaskKind,
  type ParticipationTaskStatus,
} from "@mg/schemas/src/christo/participationTask";

import {
  type AppendProjectFeedPostRequest,
  type AppendProjectFeedPostResponse,
  type GetProjectReviewsResponse,
  type ListProjectFeedResponse,
  type ListProjectTimelineResponse,
  type ResolveProjectSuggestionRequest,
  type ResolveProjectSuggestionResponse,
} from "./askPunttProjectFeed.types";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Ask Puntt Projects service — API client for the first-class project
 * container (project-scoped shared chat + personal chats + versioned
 * documents & context). Mirrors services/askPuntt.ts.
 */

const BASE = `${API_URL}/private/catalyst/ask-puntt/projects`;

export async function listProjects(params?: {
  limit?: number;
  offset?: number;
  scope?: AskPunttProjectScope;
  search?: string;
  participantIds?: string[];
  regions?: string[];
  tags?: string[];
  sortBy?: "updatedAt" | "createdAt" | "name";
  /** Opt-in to per-project coarse `reviewState` (extra ticket hydration). */
  includeReviewState?: boolean;
}): Promise<ListAskPunttProjectsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  if (params?.scope) qs.set("scope", params.scope);
  if (params?.includeReviewState) qs.set("includeReviewState", "true");
  const search = params?.search?.trim();
  if (search) qs.set("search", search);
  const participantIds = Array.from(
    new Set(
      (params?.participantIds ?? [])
        .map((id) => id.trim())
        .filter((id) => id.length > 0),
    ),
  );
  for (const id of participantIds) {
    qs.append("participantIds", id);
  }
  if (params?.regions?.length) qs.set("regions", params.regions.join(","));
  if (params?.tags?.length) qs.set("tags", params.tags.join(","));
  if (
    params?.sortBy === "updatedAt" ||
    params?.sortBy === "createdAt" ||
    params?.sortBy === "name"
  ) {
    qs.set("sortBy", params.sortBy);
  }
  const query = qs.toString();
  const json = await smartFetch(`${BASE}${query ? `?${query}` : ""}`, {
    method: "GET",
  });
  return json as ListAskPunttProjectsResponse;
}

export async function getProject(
  projectId: string,
): Promise<GetAskPunttProjectResponse> {
  const json = await smartFetch(`${BASE}/${projectId}`, { method: "GET" });
  return json as GetAskPunttProjectResponse;
}

export async function createProject(payload: {
  name: string;
  description?: string;
  context?: string;
  upcomingWork?: string;
}): Promise<CreateAskPunttProjectResponse> {
  const json = await smartFetch(BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as CreateAskPunttProjectResponse;
}

export async function updateProject(
  projectId: string,
  payload: {
    name?: string;
    description?: string;
    context?: string;
    upcomingWork?: string;
  },
): Promise<UpdateAskPunttProjectResponse> {
  const json = await smartFetch(`${BASE}/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return json as UpdateAskPunttProjectResponse;
}

export async function deleteProject(
  projectId: string,
): Promise<DeleteAskPunttProjectResponse> {
  const json = await smartFetch(`${BASE}/${projectId}`, { method: "DELETE" });
  return json as DeleteAskPunttProjectResponse;
}

export async function addProjectMembers(
  projectId: string,
  payload: { userIds?: string[]; emails?: string[] },
): Promise<AddAskPunttProjectMemberResponse> {
  const json = await smartFetch(`${BASE}/${projectId}/members`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as AddAskPunttProjectMemberResponse;
}

export async function removeProjectMember(
  projectId: string,
  userId: string,
): Promise<RemoveAskPunttProjectMemberResponse> {
  const json = await smartFetch(`${BASE}/${projectId}/members/${userId}`, {
    method: "DELETE",
  });
  return json as RemoveAskPunttProjectMemberResponse;
}

export async function shareProjectChat(
  projectId: string,
  chatId: string,
): Promise<ShareAskPunttProjectChatResponse> {
  const json = await smartFetch(`${BASE}/${projectId}/chats/${chatId}/share`, {
    method: "POST",
  });
  return json as ShareAskPunttProjectChatResponse;
}

export async function addProjectFile(
  projectId: string,
  payload: {
    name: string;
    type: "image" | "video" | "file";
    key: string;
    source: string;
  },
): Promise<AddAskPunttProjectFileResponse> {
  const json = await smartFetch(`${BASE}/${projectId}/files`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as AddAskPunttProjectFileResponse;
}

export async function addProjectFileVersion(
  projectId: string,
  fileId: string,
  payload: { key: string; source: string; name?: string; label?: string },
): Promise<AddAskPunttProjectFileVersionResponse> {
  const json = await smartFetch(
    `${BASE}/${projectId}/files/${fileId}/versions`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as AddAskPunttProjectFileVersionResponse;
}

export async function listProjectChats(
  projectId: string,
  params?: { limit?: number; offset?: number },
): Promise<ListAskPunttProjectChatsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  const json = await smartFetch(
    `${BASE}/${projectId}/chats${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return json as ListAskPunttProjectChatsResponse;
}

/**
 * Promote/attach a chat to a project (owner-only). Used by the
 * "Share to project" flow to move a personal chat into the shared scope.
 */
export async function addChatToProject(
  chatId: string,
  projectId: string,
): Promise<AddChatToAskPunttProjectResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/project`,
    { method: "POST", body: JSON.stringify({ projectId }) },
  );
  return json as AddChatToAskPunttProjectResponse;
}

/** Detach a chat from its project (owner-only). */
export async function removeChatFromProject(
  chatId: string,
): Promise<RemoveChatFromAskPunttProjectResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/project`,
    { method: "DELETE" },
  );
  return json as RemoveChatFromAskPunttProjectResponse;
}

// ---------------------------------------------------------------------------
// Project feed + reviews (Projects Overall). Types are FE-local until the
// backend worker lands the matching @mg/schemas definitions — see
// askPunttProjectFeed.types.ts for the assumed request/response shapes.
// ---------------------------------------------------------------------------

/** List a project's feed events (paginated, newest first). */
export async function listProjectFeed(
  projectId: string,
  params?: { limit?: number; offset?: number },
): Promise<ListProjectFeedResponse> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  const json = await smartFetch(
    `${BASE}/${projectId}/feed${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return json as ListProjectFeedResponse;
}

/**
 * Unified project timeline: persisted feed events plus unmirrored shared-chat
 * turns, sorted oldest-first for the feed-first UI.
 */
export async function listProjectTimeline(
  projectId: string,
  params?: { limit?: number; after?: string },
): Promise<ListProjectTimelineResponse> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.after) qs.set("after", params.after);
  const query = qs.toString();
  const json = await smartFetch(
    `${BASE}/${projectId}/timeline${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return json as ListProjectTimelineResponse;
}

/** Append a user post (or system event) to a project's feed. */
export async function appendProjectFeedPost(
  projectId: string,
  payload: AppendProjectFeedPostRequest,
): Promise<AppendProjectFeedPostResponse> {
  const json = await smartFetch(`${BASE}/${projectId}/feed`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as AppendProjectFeedPostResponse;
}

/** Aggregate active reviews/tickets for the project's right-hand panel. */
export async function getProjectReviews(
  projectId: string,
): Promise<GetProjectReviewsResponse> {
  type ReviewFileDto = {
    source: string;
    name?: string;
    type?: string;
    thumbnailUrl?: string | null;
  };
  const json = (await smartFetch(`${BASE}/${projectId}/reviews`, {
    method: "GET",
  })) as {
    status: string;
    reviews: {
      ticketId?: string | null;
      fileId?: string;
      fileName?: string;
      title?: string;
      version?: number;
      thumbnailUrl?: string | null;
      status?: string;
      reviewFiles?: ReviewFileDto[];
      contextAssets?: ReviewFileDto[];
      aiCommentCount?: number;
      humanCommentCount?: number;
      approval?: {
        decision: "approved" | "changes_requested";
        reviewerName?: string;
        at?: string;
      } | null;
    }[];
  };
  return {
    status: json.status,
    reviews: (json.reviews ?? []).map((r) => ({
      // Stable per-version id when both fileId and version are present.
      id:
        r.fileId != null && r.version != null
          ? `${r.fileId}-v${r.version}`
          : (r.fileId ?? r.ticketId ?? "review"),
      ticketId: r.ticketId ?? undefined,
      fileId: r.fileId,
      name: r.title ?? r.fileName ?? "Review",
      title: r.title,
      versionNumber: r.version,
      thumbnailUrl: r.thumbnailUrl ?? null,
      status: r.status,
      reviewFiles: r.reviewFiles,
      contextAssets: r.contextAssets,
      aiCommentCount: r.aiCommentCount ?? 0,
      humanCommentCount: r.humanCommentCount ?? 0,
      approval: r.approval ?? null,
    })),
  };
}

/**
 * Read the participation projection + recent events for a file version.
 */
export async function getApprovalParticipation(
  projectId: string,
  versionId: string,
  fileId?: string,
): Promise<GetApprovalParticipationResponse> {
  const qs = new URLSearchParams();
  if (fileId) qs.set("fileId", fileId);
  const query = qs.toString();
  const json = await smartFetch(
    `${BASE}/${projectId}/versions/${versionId}/participation${
      query ? `?${query}` : ""
    }`,
    { method: "GET" },
  );
  return json as GetApprovalParticipationResponse;
}

export type AppendApprovalEventBody = {
  type:
    | "position_recorded"
    | "note_added"
    | "participant_invited"
    | "invitation_declined"
    | "position_withdrawn"
    | "participant_suggested";
  fileId: string;
  versionId: string;
  ticketId?: string | null;
  idempotencyKey: string;
  position?: "approved" | "changes_requested" | null;
  noteBody?: string;
  parentNoteId?: string | null;
  subjectUserId?: string | null;
  suggestedRole?: string | null;
  reasoning?: string | null;
  source?: "app" | "agent";
};

/**
 * Append one approval participation event (single write path).
 */
export async function appendApprovalEvent(
  projectId: string,
  versionId: string,
  body: AppendApprovalEventBody,
): Promise<AppendApprovalEventResponse> {
  const json = await smartFetch(
    `${BASE}/${projectId}/versions/${versionId}/approval-events`,
    {
      method: "POST",
      body: JSON.stringify({ ...body, source: body.source ?? "app" }),
    },
  );
  return json as AppendApprovalEventResponse;
}

const PARTICIPATION_TASKS_BASE = `${API_URL}/private/catalyst/ask-puntt/participation-tasks`;

/**
 * List the current user's participation inbox (optionally filtered by project).
 */
export async function listParticipationTasks(params?: {
  status?: ParticipationTaskStatus;
  kind?: ParticipationTaskKind;
  projectId?: string;
  limit?: number;
}): Promise<ListParticipationTasksResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.kind) qs.set("kind", params.kind);
  if (params?.projectId) qs.set("projectId", params.projectId);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  const json = await smartFetch(
    `${PARTICIPATION_TASKS_BASE}${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return json as ListParticipationTasksResponse;
}

/**
 * Resolve a feed suggestion (ignore | approve | adjust). Approve triggers a
 * server-side action; ignore persists a dismissal; adjust carries an inline
 * payload (different person/stage/integration).
 */
export async function resolveProjectSuggestion(
  projectId: string,
  eventId: string,
  payload: ResolveProjectSuggestionRequest,
): Promise<ResolveProjectSuggestionResponse> {
  const json = await smartFetch(
    `${BASE}/${projectId}/feed/${eventId}/resolve`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as ResolveProjectSuggestionResponse;
}

/**
 * Promote a standalone chat into a new project (idempotent — one promotion
 * per chat). Bootstraps the feed from the chat summary server-side.
 */
export async function promoteChatToProject(
  chatId: string,
): Promise<PromoteChatToProjectResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/promote-to-project`,
    { method: "POST" },
  );
  return json as PromoteChatToProjectResponse;
}
